/**
 * Manual Sidebar Component
 *
 * Displays table of contents with scroll tracking and navigation.
 */

import { useState } from 'react';
import type { TableOfContentItem } from '../../types/manual';
import { scrollToHeading } from '../../utils/markdown';
import './ManualSidebar.css';

interface ManualSidebarProps {
  /** Table of contents items */
  tableOfContents: TableOfContentItem[];

  /** Currently active heading ID */
  activeHeadingId?: string;
}

export const ManualSidebar = ({ tableOfContents, activeHeadingId = '' }: ManualSidebarProps) => {
  const [expandedH2s, setExpandedH2s] = useState<Set<string>>(new Set());

  const handleHeadingClick = (itemId: string) => {
    scrollToHeading(itemId);
  };

  // Toggle individual H2
  const toggleH2 = (h2Id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const newExpanded = new Set(expandedH2s);
    if (newExpanded.has(h2Id)) {
      newExpanded.delete(h2Id);
    } else {
      newExpanded.add(h2Id);
    }
    setExpandedH2s(newExpanded);
  };

  // Expand/collapse all H2s
  const toggleAllH2s = () => {
    const h2Ids = tableOfContents
      .filter(item => item.level === 2)
      .map(item => item.id);

    if (h2Ids.length === 0) return;

    // If all H2s are expanded, collapse all; otherwise expand all
    const allExpanded = h2Ids.every(id => expandedH2s.has(id));
    const newExpanded = allExpanded ? new Set<string>() : new Set(h2Ids);
    setExpandedH2s(newExpanded);
  };

  if (tableOfContents.length === 0) {
    return null;
  }

  // Helper to find the parent H2 ID for a given item
  const findParentH2Id = (index: number): string | null => {
    for (let i = index - 1; i >= 0; i--) {
      const item = tableOfContents[i];
      if (item.level === 2) {
        return item.id;
      }
      if (item.level === 1) {
        return null;
      }
    }
    return null;
  };

  // Helper to check if an H2 has H3+ children
  const hasH3Children = (h2Index: number): boolean => {
    // Check if any item after this H2 (until next H1 or H2) is level 3+
    for (let i = h2Index + 1; i < tableOfContents.length; i++) {
      const item = tableOfContents[i];
      if (item.level === 1 || item.level === 2) {
        return false; // Hit another H1 or H2, no H3 children found
      }
      if (item.level >= 3) {
        return true; // Found an H3+
      }
    }
    return false;
  };

  // Get all H2 IDs for determining expand/collapse all state
  const h2Ids = tableOfContents
    .filter(item => item.level === 2)
    .map(item => item.id);

  const allExpanded = h2Ids.length > 0 && h2Ids.every(id => expandedH2s.has(id));

  return (
    <aside className="manual-sidebar">
      <div className="manual-sidebar__header">
        <h3 className="manual-sidebar__title">目錄</h3>
        {h2Ids.length > 0 && (
          <button
            className="manual-sidebar__toggle"
            onClick={toggleAllH2s}
            aria-label={allExpanded ? '收起全部' : '展開全部'}
          >
            {allExpanded ? '−' : '+'}
          </button>
        )}
      </div>

      <nav className="manual-sidebar__nav" aria-label="Table of Contents">
        <ul className="manual-sidebar__list">
          {tableOfContents.map((item, index) => {
            const isH2 = item.level === 2;
            const isH3OrBelow = item.level >= 3;

            // Determine if this item should be visible
            let isVisible = true;
            let parentH2Id: string | null = null;

            if (isH3OrBelow) {
              parentH2Id = findParentH2Id(index);
              isVisible = parentH2Id ? expandedH2s.has(parentH2Id) : true;
            }

            if (!isVisible) return null;

            // Check if H2 has H3 children
            const shouldShowToggle = isH2 && hasH3Children(index);

            return (
              <li
                key={item.id}
                className={`manual-sidebar__item manual-sidebar__item--level-${item.level} ${
                  activeHeadingId === item.id ? 'manual-sidebar__item--active' : ''
                }`}
              >
                <div className="manual-sidebar__link-wrapper">
                  <a
                    href={`#${item.id}`}
                    className="manual-sidebar__link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleHeadingClick(item.id);
                    }}
                    aria-current={activeHeadingId === item.id ? 'location' : undefined}
                  >
                    {item.title}
                  </a>
                  {shouldShowToggle && (
                    <button
                      className="manual-sidebar__item-toggle"
                      onClick={(e) => toggleH2(item.id, e)}
                      aria-label={expandedH2s.has(item.id) ? '收起子標題' : '展開子標題'}
                      aria-expanded={expandedH2s.has(item.id)}
                    >
                      {expandedH2s.has(item.id) ? '−' : '+'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};
